import React, { FC } from 'react';
import { ButtonBase, Stack, styled, SvgIcon, Typography, useTheme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './MenuComponent.module.scss';
import boddVariables from '../../variables.module.scss';
import MeasurementIcon from '../../assets/measurement.svg';
import SizeMatchIcon from '../../assets/sizematch.svg';
import WellnessIcon from '../../assets/physiological.svg';
import BodyCompositionIcon from '../../assets/body-composition.svg';

interface MenuItem {
    key: string,
    title: string,
    description: string,
    icon: React.ReactNode,
    link: string,
    isSampleData?: boolean | null,
}

const MenuButton = styled(ButtonBase)({
    justifyContent: 'flex-start',
});

interface MenuComponentProps {
    hasSizeMatch: boolean;
    hasMeasurements: boolean;
    hasWellness: boolean;
    hasBodyComposition: boolean;
}

const MenuComponent: FC<MenuComponentProps> = (props) => {
    const { hasSizeMatch, hasMeasurements, hasWellness, hasBodyComposition } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const menuItems: MenuItem[] = [
        ...hasSizeMatch ? [{
            key: '1',
            title: 'SizeMatch',
            description: 'Your size across all products',
            icon: (<SvgIcon sx={{ height: 39, width: 39 }}><SizeMatchIcon /></SvgIcon>),
            link: '/sizematch',
        }] : [],
        ...hasMeasurements ? [{
            key: '2',
            title: 'Measurements',
            description: 'Measurements across the body',
            icon: (<SvgIcon sx={{ height: 39, width: 39 }}><MeasurementIcon /></SvgIcon>),
            link: '/measurements',
        }] : [],
        ...(hasWellness || theme.sampleData?.wellness?.enabled) ? [{
            key: '3',
            title: 'Health Vitals',
            description: 'Vitals, physiology and more',
            icon: (<SvgIcon sx={{ height: 39, width: 39 }}><WellnessIcon /></SvgIcon>),
            link: '/wellness',
            isSampleData: theme.sampleData?.wellness?.enabled ?? false,
        }] : [],
        ...(hasBodyComposition || theme.sampleData?.bodyComposition?.enabled) ? [{
            key: '4',
            title: 'Body Composition',
            description: 'Fat Mass, Body Water, BMI and more',
            icon: (<SvgIcon sx={{ height: 39, width: 39 }}><BodyCompositionIcon /></SvgIcon>),
            link: '/body-composition',
            isSampleData: theme.sampleData?.bodyComposition?.enabled ?? false,
        }] : [],
    ];

    return (
        <div className={styles.MenuComponent} data-testid="MenuComponent">
            <Stack spacing={2}>
                {menuItems.map((item) => (
                    <MenuButton
                        key={item.key}
                        sx={{
                            borderRadius: '4px',
                            bgcolor: boddVariables.grey10,
                            border: `2px solid ${boddVariables.grey25}`,
                            p: 2,
                        }}
                        onClick={() => navigate(`${item.link}${location.search}`)}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            {item.icon}
                            <Stack
                                direction="column"
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    textAlign: 'start',
                                    width: '100%',
                                }}
                            >
                                {item.isSampleData && (
                                    <Typography
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 500,
                                            lineHeight: '150%',
                                            color: boddVariables.grey10,
                                            backgroundColor: boddVariables.brandRed,
                                            py: 0.25,
                                            px: 0.75,
                                            mb: boddVariables.sp1,
                                            borderRadius: '2px',
                                        }}
                                    >
                                        SAMPLE DATA ONLY
                                    </Typography>
                                )}
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        lineHeight: '21px',
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 400,
                                        lineHeight: '21px',
                                        pt: '2px',
                                    }}
                                >
                                    {item.description}
                                </Typography>
                            </Stack>
                            <ChevronRightIcon />
                        </Stack>
                    </MenuButton>
                ))}
            </Stack>
        </div>
    );
};

export default MenuComponent;
